import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react'
import type { User, AuthState } from '../../types/auth.ts'
import { login as apiLogin, getCurrentUser } from '../../api/auth.ts'
import { setAuthToken, getAuthToken, setRefreshToken, setUnauthorizedHandler } from '../../api/client.ts'

export const AuthContext = createContext<AuthState>({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  sessionExpired: false,
  login: async () => {},
  logout: () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(getAuthToken())
  const [isLoading, setIsLoading] = useState(true)
  // D4: distinguishes an involuntary logout (refresh exhaustion) from a
  // deliberate sign-out, so ProtectedRoute can add ?reason=expired.
  const [sessionExpired, setSessionExpired] = useState(false)

  // On mount, try to restore session from stored token
  useEffect(() => {
    if (!token) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    getCurrentUser()
      .then(u => { if (!cancelled) setUser(u) })
      .catch(() => {
        if (!cancelled) {
          setAuthToken(null)
          setRefreshToken(null)
          setToken(null)
        }
      })
      .finally(() => { if (!cancelled) setIsLoading(false) })

    return () => { cancelled = true }
  }, [token])

  // Register global 401 handler so expired tokens clear auth state via React.
  // client.ts only calls this after a FAILED silent refresh (D4), so firing
  // it means the session genuinely expired.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null)
      setUser(null)
      setSessionExpired(true)
    })
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const response = await apiLogin(username, password)
    setAuthToken(response.access_token)
    setRefreshToken(response.refresh_token ?? null)
    setToken(response.access_token)
    setUser(response.user) // Use user from login response — no extra API call
    setSessionExpired(false)
  }, [])

  const logout = useCallback(() => {
    setAuthToken(null)
    setRefreshToken(null)
    setToken(null)
    setUser(null)
    // A deliberate sign-out is not an expiry.
    setSessionExpired(false)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        sessionExpired,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
